import { useEffect, useState } from "react";
import { GripVertical, Plus, Trash2, Sparkles } from "lucide-react";
import api from "../lib/api";
import CadenceStepPreview from "./CadenceStepPreview";

/**
 * Stateless cadence editor — receives a list of sequence steps + business
 * hours config and emits onChange when anything changes. Used by Campaign edit
 * page (for per-campaign overrides). Settings.jsx has its own inline copy with
 * the Smart Scheduling preview UI.
 *
 * Props:
 *   steps: SequenceStep[]
 *   businessHoursStart, businessHoursEnd: int
 *   businessDaysOnly, smartScheduling: bool
 *   onStepsChange(nextSteps), onHoursChange({ key, value })
 *   useGlobal: bool (when true, steps array is treated as "inheriting Settings")
 *   onUseGlobalChange(bool)
 *   campaignId?: string — when provided, the per-row "Try this script" button
 *     is enabled and personalizes the AI dry-run against that campaign.
 */
export default function CadenceEditor({
  steps = [],
  businessHoursStart = 9,
  businessHoursEnd = 18,
  businessDaysOnly = true,
  smartScheduling = false,
  onStepsChange,
  onHoursChange,
  useGlobal = false,
  onUseGlobalChange,
  showUseGlobalToggle = true,
  campaignId = null,
  testidPrefix = "camp-cadence",
  customAngles = null,
}) {
  const [previewStep, setPreviewStep] = useState(null);
  const [loadedAngles, setLoadedAngles] = useState([]);
  // If parent didn't pass customAngles, lazy-load them from /settings so this
  // editor still surfaces workspace-defined angles when embedded in Campaign.
  useEffect(() => {
    if (customAngles !== null) return;
    let cancelled = false;
    api.get("/settings")
      .then(({ data }) => { if (!cancelled) setLoadedAngles(data?.custom_angles || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [customAngles]);
  const angles = customAngles ?? loadedAngles;
  const updateStep = (idx, patch) => {
    onStepsChange(steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const removeStep = (idx) => {
    onStepsChange(steps.filter((_, i) => i !== idx));
  };
  const addStep = () => {
    const lastDay = steps.length ? Math.max(...steps.map((x) => x.day_offset ?? 0)) : 0;
    onStepsChange([
      ...steps,
      {
        step: steps.length + 1,
        day_offset: lastDay + 2,
        hour: 11,
        minute: 0,
        kind: "sms",
        angle: "value",
      },
    ]);
  };

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-editor`}>
      {/* Inherit-vs-override toggle (hidden for variant B where the toggle
          doesn't make sense — variant B always overrides). */}
      {showUseGlobalToggle && (
        <label
          className="flex items-start gap-3 cursor-pointer border border-zinc-200 p-3 hover:border-zinc-400"
          data-testid={`${testidPrefix}-use-global`}
        >
          <input
            type="checkbox"
            checked={useGlobal}
            onChange={(e) => onUseGlobalChange(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#002FA7]"
          />
          <div>
            <div className="text-sm font-bold uppercase tracking-wider">
              Use global cadence from Settings
            </div>
            <p className="text-xs text-zinc-600 mt-1">
              On = this campaign uses the default cadence you set in <em>Settings → Outreach Cadence</em>.
              Off = define a custom cadence below (overrides global for this campaign only).
            </p>
          </div>
        </label>
      )}

      {!useGlobal && (
        <>
          {/* Business hours row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`${testidPrefix}-business-hours`}>
            <Field label="Start hour (local)">
              <input
                type="number" min={0} max={23}
                value={businessHoursStart}
                onChange={(e) => onHoursChange({ key: "business_hours_start", value: Number(e.target.value) })}
                data-testid={`${testidPrefix}-bh-start`}
                className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]"
              />
            </Field>
            <Field label="End hour (local, excl.)">
              <input
                type="number" min={0} max={23}
                value={businessHoursEnd}
                onChange={(e) => onHoursChange({ key: "business_hours_end", value: Number(e.target.value) })}
                data-testid={`${testidPrefix}-bh-end`}
                className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]"
              />
            </Field>
            <Field label="Weekdays only?">
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={businessDaysOnly}
                  onChange={(e) => onHoursChange({ key: "business_days_only", value: e.target.checked })}
                  data-testid={`${testidPrefix}-weekdays-only`}
                  className="w-4 h-4 accent-[#002FA7]"
                />
                <span className="text-xs">Skip weekends</span>
              </label>
            </Field>
            <Field label="Smart scheduling?">
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={smartScheduling}
                  onChange={(e) => onHoursChange({ key: "smart_scheduling", value: e.target.checked })}
                  data-testid={`${testidPrefix}-smart`}
                  className="w-4 h-4 accent-[#002FA7]"
                />
                <span className="text-xs">Bias to learned best hour</span>
              </label>
            </Field>
          </div>

          {/* Steps table */}
          <div className="border border-zinc-200" data-testid={`${testidPrefix}-table`}>
            <div className="grid grid-cols-[40px_1fr_1fr_1fr_1.5fr_70px_40px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <div>#</div>
              <div>Day</div>
              <div>Time (local)</div>
              <div>Channel</div>
              <div>Angle (AI hint)</div>
              <div>Preview</div>
              <div></div>
            </div>
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="grid grid-cols-[40px_1fr_1fr_1fr_1.5fr_70px_40px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 items-center"
                data-testid={`${testidPrefix}-row-${idx}`}
              >
                <div className="text-xs font-mono text-zinc-500 flex items-center gap-1">
                  <GripVertical className="w-3 h-3" /> {idx + 1}
                </div>
                <input
                  type="number" min={0} max={30}
                  value={step.day_offset ?? 0}
                  onChange={(e) => updateStep(idx, { day_offset: Number(e.target.value) })}
                  data-testid={`${testidPrefix}-day-${idx}`}
                  className="px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number" min={0} max={23}
                    value={step.hour ?? 10}
                    onChange={(e) => updateStep(idx, { hour: Number(e.target.value) })}
                    data-testid={`${testidPrefix}-hour-${idx}`}
                    className="w-12 px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
                  />
                  <span className="text-xs text-zinc-400">:</span>
                  <input
                    type="number" min={0} max={59}
                    value={step.minute ?? 0}
                    onChange={(e) => updateStep(idx, { minute: Number(e.target.value) })}
                    data-testid={`${testidPrefix}-minute-${idx}`}
                    className="w-12 px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
                  />
                </div>
                <select
                  value={step.kind || "sms"}
                  onChange={(e) => updateStep(idx, { kind: e.target.value })}
                  data-testid={`${testidPrefix}-kind-${idx}`}
                  className="px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7] bg-white"
                >
                  <option value="sms">SMS</option>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="mark_lost">Mark Lost</option>
                </select>
                <select
                  value={step.angle || ""}
                  onChange={(e) => updateStep(idx, { angle: e.target.value })}
                  data-testid={`${testidPrefix}-angle-${idx}`}
                  className="px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7] bg-white"
                >
                  <option value="">— none —</option>
                  <optgroup label="Built-in">
                    <option value="intro">intro (first touch)</option>
                    <option value="value">value (new angle)</option>
                    <option value="first_attempt">first call attempt</option>
                    <option value="second_attempt">second call attempt</option>
                    <option value="breakup">breakup (soft goodbye)</option>
                    <option value="last_call">last call</option>
                  </optgroup>
                  {angles && angles.length > 0 && (
                    <optgroup label="Custom (from Settings)">
                      {angles.filter((a) => a.key).map((a) => (
                        <option key={a.key} value={a.key}>{a.label || a.key}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <button
                  type="button"
                  onClick={() => setPreviewStep({ ...step, step: step.step ?? idx + 1 })}
                  disabled={step.kind !== "sms" && step.kind !== "call"}
                  data-testid={`${testidPrefix}-preview-${idx}`}
                  title={
                    step.kind === "sms" || step.kind === "call"
                      ? "Dry-run with Claude — no SMS sent"
                      : "Only sms/call steps can be previewed"
                  }
                  className="flex items-center justify-center gap-1 px-2 py-1.5 border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] text-zinc-600 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-300 disabled:hover:text-zinc-600"
                >
                  <Sparkles className="w-3 h-3" /> Try
                </button>
                <button
                  type="button"
                  onClick={() => removeStep(idx)}
                  data-testid={`${testidPrefix}-remove-${idx}`}
                  className="text-zinc-400 hover:text-red-600 flex items-center justify-center"
                  title="Remove this touch"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {steps.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-zinc-500 font-mono">
                No custom steps — click below to add the first touch.
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={addStep}
            data-testid={`${testidPrefix}-add-step`}
            className="px-3 py-2 border border-zinc-300 hover:bg-zinc-50 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add touchpoint
          </button>
        </>
      )}
      {previewStep && (
        <CadenceStepPreview
          step={previewStep}
          campaignId={campaignId}
          onClose={() => setPreviewStep(null)}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
